import React from 'react'
import { useOdds } from '../context/OddsContext'

interface OddsDisplayProps {
  odds: number
  isBest: boolean
  bestOdds?: number | null
  bestBookmaker?: string | null
  bookmakerName?: string | null
  size?: 'sm' | 'md' | 'lg'
  showBestIndicator?: boolean
}

export function OddsDisplay({
  odds,
  isBest,
  bestOdds,
  bestBookmaker,
  bookmakerName,
  size = 'md',
  showBestIndicator = true,
}: OddsDisplayProps) {
  const { formatOdds, oddsHidden } = useOdds()

  if (oddsHidden) {
    return null
  }

  const sizeClass = `odds-display-${size}`

  return (
    <span className={`odds-display ${sizeClass} ${isBest ? 'is-best' : 'not-best'}`} title={bookmakerName || undefined}>
      <span className="odds-value">{formatOdds(odds)}</span>
      {showBestIndicator && !isBest && bestOdds && (
        <span
          className="best-indicator"
          title={`Best: ${formatOdds(bestOdds)} @ ${bestBookmaker}`}
        >
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
          {formatOdds(bestOdds)}
        </span>
      )}
    </span>
  )
}

interface OddsBadgeProps {
  fixtureId: number
  marketSlug: string
  outcome: string
  line?: number | null
  size?: 'sm' | 'md' | 'lg'
}

export function OddsBadge({
  fixtureId,
  marketSlug,
  outcome,
  line,
  size = 'sm',
}: OddsBadgeProps) {
  const {
    preferredBookmaker,
    getOddsForBookmaker,
    getBestOddsForSelection,
    formatOdds,
    oddsHidden,
  } = useOdds()

  if (oddsHidden || !preferredBookmaker) {
    return null
  }

  const preferredOdds = getOddsForBookmaker(
    fixtureId,
    marketSlug,
    outcome,
    preferredBookmaker.slug,
    line
  )

  const bestInfo = getBestOddsForSelection(fixtureId, marketSlug, outcome, line)

  if (preferredOdds === null) {
    const sizeClass = `odds-display-${size}`
    return (
      <span className={`odds-display ${sizeClass}`}>
        <span className="odds-value">-</span>
      </span>
    )
  }

  const isBest = bestInfo?.bookmaker_slug === preferredBookmaker.slug

  return (
    <OddsDisplay
      odds={preferredOdds}
      isBest={isBest}
      bestOdds={isBest ? null : bestInfo?.odds}
      bestBookmaker={isBest ? null : bestInfo?.bookmaker_name}
      bookmakerName={preferredBookmaker.name}
      size={size}
    />
  )
}
